/**
 * GitHooksSetupWizard Component
 *
 * Setup wizard for installing LeftShield git hooks.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  tokens,
  Divider,
  MessageBar,
  MessageBarBody,
  Link,
} from '@fluentui/react-components';
import {
  CopyRegular,
  CheckmarkRegular,
  DocumentRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: '600px',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  codeBlock: {
    position: 'relative',
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
  },
  copyButton: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalXS,
  },
  configExample: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
  },
});

interface GitHooksSetupWizardProps {
  open: boolean;
  onClose: () => void;
  apiKey?: string;
}

export const GitHooksSetupWizard: React.FC<GitHooksSetupWizardProps> = ({
  open,
  onClose,
  apiKey = 'YOUR_API_KEY',
}) => {
  const styles = useStyles();
  const [copiedStep, setCopiedStep] = useState<number | null>(null);

  const copyToClipboard = (text: string, step: number) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const installCommand = 'npm install --save-dev @leftshield/git-hooks';
  const initCommand = 'npx @leftshield/git-hooks install';
  const configExample = `{
  "apiKey": "\${LEFTSHIELD_API_KEY}",
  "baseUrl": "https://api.leftshield.io",
  "hooks": {
    "pre-commit": {
      "enabled": true,
      "failOn": "block",
      "exclude": ["*.md", "*.json", "*.lock"]
    },
    "pre-push": {
      "enabled": true,
      "failOn": "block"
    }
  }
}`;

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Set Up Git Hooks</DialogTitle>
          <DialogContent className={styles.content}>
            <MessageBar intent="info">
              <MessageBarBody>
                Git hooks validate your code before commits and pushes, catching compliance issues early in the development process.
              </MessageBarBody>
            </MessageBar>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>1</span>
                <Text weight="semibold">Install the package</Text>
              </div>
              <div className={styles.codeBlock}>
                <code>{installCommand}</code>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 1 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(installCommand, 1)}
                />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>2</span>
                <Text weight="semibold">Set your API key</Text>
              </div>
              <Text size={200}>
                Set the <code>LEFTSHIELD_API_KEY</code> environment variable:
              </Text>
              <div className={styles.codeBlock}>
                <code>export LEFTSHIELD_API_KEY={apiKey}</code>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 2 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(`export LEFTSHIELD_API_KEY=${apiKey}`, 2)}
                />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>3</span>
                <Text weight="semibold">Initialize hooks</Text>
              </div>
              <div className={styles.codeBlock}>
                <code>{initCommand}</code>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 3 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(initCommand, 3)}
                />
              </div>
            </div>

            <Divider />

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <DocumentRegular />
                <Text weight="semibold">Configuration (optional)</Text>
              </div>
              <Text size={200}>
                Create a <code>.leftshieldrc.json</code> file in your project root to customize behavior:
              </Text>
              <pre className={styles.configExample}>{configExample}</pre>
            </div>

            <MessageBar intent="success">
              <MessageBarBody>
                Once installed, the hooks will automatically validate code on every commit and push.{' '}
                <Link href="https://docs.leftshield.io/git-hooks" target="_blank">
                  View documentation
                </Link>
              </MessageBarBody>
            </MessageBar>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
